import React, { useEffect, useMemo, useState } from "react";
import useApi from "../../CustomHooks/useApi";
import { ufgAPI } from "../../../api/apiClient";
import useUfgMonth from "./hooks/useUfgMonth";
import GroupTable from "./components/GroupTable";
import ExcelTable from "./components/ExcelTable";
import "../../../css/Views/UfgSettlementsView.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import MonthControls from "./components/MonthControls";
import Loading from "../../../Loading";
import ActionIcon from "../../Trips/Participants/ActionIcon";
import * as FaIcons from "react-icons/fa";

const UfgSettlementsView = () => {
  const { callApi } = useApi();
  const { year, month, monthValue, prevMonth, nextMonth, setFromInput } = useUfgMonth();

  const [grouped, setGrouped] = useState(null);
  const [excelRows, setExcelRows] = useState([]);
  const [filteredForExport, setFilteredForExport] = useState(null);
  const [excelMode, setExcelMode] = useState("reservations");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [exportOpen, setExportOpen] = useState(false);
  const [exportMode, setExportMode] = useState("month");
  const [fromDT, setFromDT] = useState("");
  const [toDT, setToDT] = useState("");

  const monthName = useMemo(
    () => new Date(year, month - 1, 1).toLocaleDateString("pl-PL", { month: "long" }),
    [year, month]
  );
  const monthLabel = useMemo(
    () => new Date(year, month - 1, 1).toLocaleDateString("pl-PL", { month: "long", year: "numeric" }),
    [year, month]
  );

  const todayStr = useMemo(() => new Date().toLocaleDateString("pl-PL"), []);

  const inMonth = (date, start, end) => {
    if (!date) return false;
    const d = new Date(date);
    return d >= start && d < end;
  };

  const buildGrouped = (rows, y, m) => {
    const start = new Date(y, m - 1, 1);
    const end = new Date(y, m, 1);
    const map = new Map();
    for (const r of rows ?? []) {
      const key = String(r.tripGuid);
      if (!map.has(key)) {
        map.set(key, {
          tripGuid: r.tripGuid,
          tripName: r.tripName,
          confirmed_Count: 0,
          confirmedBefore_CancelledInM_Count: 0,
          confirmedInM_CancelledInM_Count: 0,
          confirmed_People: 0,
          confirmedBefore_CancelledInM_People: 0,
          confirmedInM_CancelledInM_People: 0,
        });
      }
      const row = map.get(key);
      const confirmedInM = inMonth(r.confirmedAt, start, end);
      const cancelledInM = !!r.cancelledAtEom;
      if (confirmedInM && !cancelledInM) row.confirmed_Count += 1;
      if (!confirmedInM && cancelledInM) row.confirmedBefore_CancelledInM_Count += 1;
      if (confirmedInM && cancelledInM) row.confirmedInM_CancelledInM_Count += 1;
      row.confirmed_People += r.confirmed_People ?? 0;
      row.confirmedBefore_CancelledInM_People += r.confirmedBefore_CancelledInM_People ?? 0;
      row.confirmedInM_CancelledInM_People += r.confirmedInM_CancelledInM_People ?? 0;
    }
    return Array.from(map.values()).sort((a, b) => (a.tripName || "").localeCompare(b.tripName || ""));
  };

  const loadAll = async () => {
    setBusy(true);
    try {
      await callApi(
        () => ufgAPI.getMonthGrouped({ year, month }),
        (excelData) => {
          setExcelRows(excelData || []);
          const g = buildGrouped(excelData || [], year, month);
          setGrouped({ rows: g });
        }
      );
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [year, month]); 

  const tripOptions = useMemo(() => {
    const uniq = new Map();
    (excelRows ?? []).forEach((r) => {
      if (!uniq.has(r.tripGuid)) uniq.set(r.tripGuid, { tripGuid: r.tripGuid, tripName: r.tripName });
    });
    return Array.from(uniq.values()).sort((a, b) => (a.tripName || "").localeCompare(b.tripName || ""));
  }, [excelRows]);

  const toLocalDT = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openExportPopup = () => {
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 1);
    setExportMode("month");
    setFromDT(toLocalDT(new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0)));
    setToDT(toLocalDT(new Date(end.getFullYear(), end.getMonth(), end.getDate(), 0, 0, 0)));
    setExportOpen(true);
  };

  const handleExport = async () => {
    try {
      setExporting(true);

      const params = {};
      if (exportMode === "all") {
        params.all = true;
      } else {
        if (!fromDT || !toDT) throw new Error("Uzupełnij daty.");
        const fromIso = fromDT.length === 16 ? `${fromDT}:00` : fromDT;
        const toIso = toDT.length === 16 ? `${toDT}:00` : toDT;
        params.from = fromIso;
        params.to = toIso;
      }

      const resp = await ufgAPI.exportXlsx(params.from, params.to, params.all);
      const blob = resp.data;

      const stamp =
        exportMode === "month"
          ? `${year}-${String(month).padStart(2, "0")}`
          : exportMode === "range"
          ? `${fromDT?.slice(0, 10)}_${toDT?.slice(0, 10)}`
          : "ALL";

      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `UFG_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 0);

      setExportOpen(false);
    } catch (e) {
      console.error(e);
      alert(e.message || "Błąd eksportu.");
    } finally {
      setExporting(false);
    }
  };

  const exportDisabled =
    exporting ||
    (exportMode === "range" && (!fromDT || !toDT || new Date(fromDT) >= new Date(toDT))) ||
    (exportMode === "month" && (!fromDT || !toDT));

  const asButtonDivProps = (onActivate, disabled = false) => ({
    role: "button",
    tabIndex: disabled ? -1 : 0,
    onClick: disabled ? undefined : onActivate,
    onKeyDown: disabled
      ? undefined
      : (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onActivate();
          }
        },
    "aria-disabled": disabled || undefined,
    style: disabled ? { opacity: 0.6, pointerEvents: "none" } : undefined,
  });

  return (
    <>
      <div className="ufg-header">
        <div className="ufg-controls">
          <MonthControls
            monthValue={monthValue}
            onMonthChange={setFromInput}
            onPrev={prevMonth}
            onNext={nextMonth}
            onRefreshAll={loadAll}
          />
          <ActionIcon
            icon={<FaIcons.FaFileExcel />}
            label="Eksportuj aktualne"
            active
            disabled={!((filteredForExport ?? excelRows)?.length)}
            onClick={openExportPopup}
          />
        </div>
      </div>

      {busy ? (
        <Loading text="Ładowanie danych" />
      ) : (
        <div className="ufg-content">
          <div className="ufg-two-col">
            <GroupTable title="Statystyki rezerwacji" rows={grouped?.rows} mode="res" monthName={monthName} />
            <GroupTable title="Statystyki os. uczestniczących" rows={grouped?.rows} mode="people" monthName={monthName} />
          </div>


          <ExcelTable
            rows={excelRows}
            trips={tripOptions}
            year={year}
            month={month}
            monthLabel={monthLabel}
            mode={excelMode}
            onFilterChange={setFilteredForExport}
            onModeChange={setExcelMode}
          />
        </div>
      )}

      {exportOpen && (
        <div className="ufg-modal-backdrop" onClick={() => !exporting && setExportOpen(false)}>
          <div className="ufg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ufg-modal-header">
              <div className="ufg-modal-title">Eksport do Excel</div>
              <div className="ufg-modal-close" role="button" tabIndex={0}
                  onClick={() => !exporting && setExportOpen(false)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && !exporting && setExportOpen(false)}>
                ×
              </div>
            </div>

            <div className="ufg-modal-body">
            <div
              style={{
                background: "#F3F6FA",
                border: "1px solid #e5e7eb",
                color: "#374151",
                padding: 10,
                borderRadius: 10,
                marginBottom: 12,
                fontSize: 14,
                lineHeight: 1.35,
              }}
            >
              Eksport obejmuje <b>aktualny stan rezerwacji</b> na dzień {todayStr}.<br />
              Wybierany niżej zakres dotyczy <b>daty potwierdzenia rezerwacji</b>.
            </div>

            <div className="ufg-radio-group">
              <label className="ufg-radio">
                <input
                  type="radio"
                  name="exportMode"
                  value="month"
                  checked={exportMode === "month"}
                  onChange={() => setExportMode("month")}
                />
                Bieżący miesiąc
                <span style={{ color: "#6b7280", marginLeft: 6, fontSize: 12 }}>
                  (po dacie potwierdzenia)
                </span>
              </label>

              <label className="ufg-radio">
                <input
                  type="radio"
                  name="exportMode"
                  value="range"
                  checked={exportMode === "range"}
                  onChange={() => setExportMode("range")}
                />
                Zakres dat
                <span style={{ color: "#6b7280", marginLeft: 6, fontSize: 12 }}>
                  (po dacie potwierdzenia)
                </span>
              </label>

              <label className="ufg-radio">
                <input
                  type="radio"
                  name="exportMode"
                  value="all"
                  checked={exportMode === "all"}
                  onChange={() => setExportMode("all")}
                />
                Wszystkie
              </label>
            </div>

            {exportMode === "range" && (
              <div className="ufg-range">
                <div className="ufg-field">
                  <label>Od</label>
                  <input
                    className="ufg-input"
                    type="datetime-local"
                    value={fromDT}
                    onChange={(e) => setFromDT(e.target.value)}
                    disabled={exportMode === "month"}
                  />
                </div>
                <div className="ufg-field">
                  <label>Do</label>
                  <input
                    className="ufg-input"
                    type="datetime-local"
                    value={toDT}
                    onChange={(e) => setToDT(e.target.value)}
                    disabled={exportMode === "month"}
                  />
                </div>
              </div>
            )}
          </div>


            <div className="ufg-modal-footer">
              <div
                className={`ufg-btn${exporting ? " is-disabled" : ""}`}
                role="button"
                tabIndex={0}
                aria-disabled={exporting ? "true" : "false"}
                onClick={() => !exporting && setExportOpen(false)}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !exporting) setExportOpen(false);
                }}
              >
                Anuluj
              </div>

              <div
                className={`ufg-btn primary${exportDisabled ? " is-disabled" : ""}`}
                role="button"
                tabIndex={0}
                aria-disabled={exportDisabled ? "true" : "false"}
                onClick={() => !exportDisabled && handleExport()}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !exportDisabled) handleExport();
                }}
              >
                {exporting ? "Eksportuję…" : "Eksportuj"}
              </div>
            </div>

          </div>
        </div>
      )}

    </>
  );
};

export default UfgSettlementsView;
